// Detección de tokens del buscador (Enter), PARAMETRIZADA por categorías
// (Ajuste #16; síntesis de la home §2). Cada página arma su lista ordenada de
// `CategoriaToken` (el Avivador: CC exacto → cliente → Control RDT → rama →
// estado de obra → nombre de obra; §13 2.5) y decide qué hace con el
// resultado (token, o volcar al popover). NO se toca `filtros-texto.ts` de la
// home ni `matchOne` de la v1.
//
// Puro: se testea en tokens.test.ts (vitest, node).
import { normText } from '@/lib/client-color';
import type { CandidatoToken, CategoriaToken, TokenDetectado } from './tipos';

/**
 * Cómo compara una categoría (ver `detectarToken`):
 * - 'exacta': solo los niveles 1 y 2 (el CC);
 * - 'exacta-o-parcial' (default): el parcial es la etiqueta que CONTIENE el texto;
 * - 'inicio-de-palabra': el parcial es la etiqueta con el texto al INICIO de
 *   alguna palabra (juez #1 de T3b: el cliente del Avivador, así «vera» no es
 *   «Privera S.A.» y «priv» sí).
 * Se suma acá el tercer modo hasta que `CategoriaToken.coincidencia` (tipos.ts,
 * del orquestador) lo incluya; con eso, `CategoriaDeteccion` = `CategoriaToken`.
 */
export type ModoCoincidencia = NonNullable<CategoriaToken<unknown>['coincidencia']> | 'inicio-de-palabra';

/** `CategoriaToken` con cualquiera de los `ModoCoincidencia`. */
export type CategoriaDeteccion<I, C extends string = string> = Omit<CategoriaToken<I, C>, 'coincidencia'> & {
  coincidencia?: ModoCoincidencia;
};

/**
 * Forma canónica para comparar y para el token de texto libre:
 * `normText(texto)` (minúsculas, sin diacríticos), `trim()` y los espacios
 * (saltos de línea incluidos) colapsados a uno. «  Sin   Entregár » → «sin entregar».
 */
export function normalizarBusqueda(texto: string): string {
  return normText(texto).replace(/\s+/g, ' ').trim();
}

/** Un candidato con sus formas ya normalizadas (se normaliza una sola vez). */
interface CandidatoNormalizado {
  candidato: CandidatoToken;
  valor: string;
  etiqueta: string;
  alias: string[];
}

/** Deduplica por `valor` crudo (gana el primero) y normaliza. */
function prepararCandidatos(candidatos: readonly CandidatoToken[]): CandidatoNormalizado[] {
  const vistos = new Set<string>();
  const out: CandidatoNormalizado[] = [];
  for (const c of candidatos) {
    if (vistos.has(c.valor)) continue;
    vistos.add(c.valor);
    out.push({
      candidato: c,
      valor: normalizarBusqueda(c.valor),
      etiqueta: normalizarBusqueda(c.etiqueta),
      alias: (c.alias ?? []).map(normalizarBusqueda),
    });
  }
  return out;
}

/** Letra o dígito de cualquier alfabeto: lo que NO corta una palabra. */
const LETRA_O_DIGITO = /[\p{L}\p{N}]/u;

/**
 * ¿`t` aparece en `texto` al INICIO de una palabra? Inicio = el principio del
 * texto o después de algo que no es letra ni dígito (espacio, punto,
 * paréntesis, guion). Los dos ya normalizados. «privera s.a.»: «priv» y «s.a»
 * sí; «vera» no. «ana vera»: «ana ve» y «vera» sí; «a vera» no.
 */
function alInicioDePalabra(texto: string, t: string): boolean {
  for (let i = texto.indexOf(t); i !== -1; i = texto.indexOf(t, i + 1)) {
    if (i === 0 || !LETRA_O_DIGITO.test(texto[i - 1])) return true;
  }
  return false;
}

/**
 * Resultado de un nivel dentro de una categoría:
 * - el candidato si hay UNO solo;
 * - 'ambigua' si hay dos o más (la categoría se abandona: se pasa a la siguiente);
 * - null si no hay ninguno (se prueba el nivel siguiente de la misma categoría).
 */
function nivel(
  candidatos: readonly CandidatoNormalizado[],
  coincide: (c: CandidatoNormalizado) => boolean,
): CandidatoNormalizado | 'ambigua' | null {
  let elegido: CandidatoNormalizado | null = null;
  for (const c of candidatos) {
    if (!coincide(c)) continue;
    if (elegido) return 'ambigua';
    elegido = c;
  }
  return elegido;
}

/**
 * Clasifica el texto del Enter. `t = normalizarBusqueda(texto)`; si `t` es ''
 * devuelve null. Si no, prueba las categorías EN ORDEN y devuelve la primera
 * que da un candidato ÚNICO; null si ninguna (= texto libre: lo arma la página).
 *
 * Por categoría, con sus candidatos deduplicados por `valor` (gana el primero)
 * y todo comparado con `normalizarBusqueda`:
 * 1. Exacta de 1er nivel: `etiqueta === t` o `valor === t`. Uno solo → ese.
 *    Dos o más → categoría ambigua: se pasa a la SIGUIENTE (no se prueban 2 ni 3).
 * 2. Exacta de 2º nivel: algún `alias === t`. Uno solo → ese; dos o más → siguiente.
 * 3. Si `coincidencia === 'exacta'` → siguiente.
 * 4. Parcial, solo contra la etiqueta (nunca `valor` ni `alias`):
 *    - 'exacta-o-parcial' (default): `etiqueta.includes(t)`;
 *    - 'inicio-de-palabra': `t` al inicio de alguna palabra de la etiqueta
 *      («vera» no es «Privera S.A.»; «priv» sí).
 *    Uno solo → ese; si no → siguiente.
 *
 * Casos que fija el contrato (DP-7, hallazgo «vera» de la QA de T1):
 * - «1408» con «1408» y «1408 UTE» presentes → «1408» (nivel 1 gana al alias).
 * - «1408» con solo «1408 UTE» → «1408 UTE» (alias).
 * - «vera» con el candidato {valor 'A. Vera', etiqueta 'Ana Vera'} → un solo
 *   candidato (la persona), no dos.
 */
export function detectarToken<I, C extends string>(
  texto: string,
  items: readonly I[],
  categorias: readonly CategoriaDeteccion<I, C>[],
): TokenDetectado<C> | null {
  const t = normalizarBusqueda(texto);
  if (t === '') return null;

  for (const cat of categorias) {
    const elegido = elegirEnCategoria(t, prepararCandidatos(cat.candidatos(items)), cat.coincidencia);
    if (elegido) {
      return { categoria: cat.clave, valor: elegido.candidato.valor, etiqueta: elegido.candidato.etiqueta };
    }
  }
  return null;
}

/** Los niveles 1 a 4 dentro de UNA categoría; null = se pasa a la siguiente
 *  (ninguno, o un nivel ambiguo, que corta la categoría entera). */
function elegirEnCategoria(
  t: string,
  candidatos: readonly CandidatoNormalizado[],
  coincidencia: ModoCoincidencia | undefined,
): CandidatoNormalizado | null {
  const exacto = nivel(candidatos, c => c.etiqueta === t || c.valor === t);
  if (exacto) return exacto === 'ambigua' ? null : exacto;

  const alias = nivel(candidatos, c => c.alias.includes(t));
  if (alias) return alias === 'ambigua' ? null : alias;

  if (coincidencia === 'exacta') return null;

  const parcial = nivel(
    candidatos,
    coincidencia === 'inicio-de-palabra' ? c => alInicioDePalabra(c.etiqueta, t) : c => c.etiqueta.includes(t),
  );
  return parcial === 'ambigua' ? null : parcial;
}
